import React from 'react';
import Icon from '../../../components/AppIcon';

const AuditLogsList = ({ logs }) => {
  const getActionIcon = (actionType) => {
    const iconMap = {
      'employee_added': 'UserPlus',
      'employee_removed': 'UserMinus',
      'employee_updated': 'UserCheck',
      'tier_changed': 'TrendingUp',
      'billing_updated': 'DollarSign'
    };
    return iconMap?.[actionType] || 'Activity';
  };

  const getActionColor = (actionType) => {
    const colorMap = {
      'employee_added': 'text-success',
      'employee_removed': 'text-error',
      'employee_updated': 'text-primary',
      'tier_changed': 'text-accent',
      'billing_updated': 'text-secondary'
    };
    return colorMap?.[actionType] || 'text-muted-foreground';
  };

  const formatActionType = (actionType) => {
    return actionType?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l?.toUpperCase());
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTierDisplay = (tierName) => {
    if (!tierName) return 'N/A';
    const tierMap = {
      'tier_1_10': '1-10 Employees',
      'tier_10_50': '10-50 Employees',
      'tier_50_500': '50-500 Employees',
      'tier_500_plus': '500+ Employees'
    };
    return tierMap?.[tierName] || tierName;
  };

  if (!logs || logs?.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Icon name="FileText" size={48} className="mx-auto mb-4 opacity-50" />
        <p>No audit logs available</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {logs?.map((log) => (
        <div
          key={log?.id}
          className="flex items-start gap-4 p-4 bg-background rounded-lg border border-border hover:border-primary/50 transition-colors"
        >
          <div className={`flex-shrink-0 ${getActionColor(log?.actionType)}`}>
            <Icon name={getActionIcon(log?.actionType)} size={20} />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 mb-1">
              <h4 className="text-sm font-semibold text-foreground">
                {formatActionType(log?.actionType)}
              </h4>
              <span className="text-xs text-muted-foreground whitespace-nowrap">
                {formatDate(log?.createdAt)}
              </span>
            </div>

            <div className="text-sm text-muted-foreground space-y-1">
              {log?.actionType === 'tier_changed' && (
                <p>
                  Tier changed from <span className="font-medium text-foreground">{getTierDisplay(log?.tierBefore)}</span> to{' '}
                  <span className="font-medium text-foreground">{getTierDisplay(log?.tierAfter)}</span>
                  {log?.billingImpactKina && (
                    <span> - New billing: <span className="font-medium text-foreground">K{log?.billingImpactKina}</span>/month</span>
                  )}
                </p>
              )}

              {(log?.actionType === 'employee_added' || log?.actionType === 'employee_removed') && (
                <p>
                  {log?.changes?.employee_number && (
                    <span className="font-medium text-foreground">{log?.changes?.employee_number}</span>
                  )}
                  {log?.changes?.name && (
                    <span> - {log?.changes?.name}</span>
                  )}
                </p>
              )}

              {log?.actionType === 'employee_updated' && log?.changes?.status_change && (
                <p>
                  Status changed from{' '}
                  <span className="font-medium text-foreground">
                    {log?.changes?.status_change?.from ? 'Active' : 'Inactive'}
                  </span>
                  {' '}to{' '}
                  <span className="font-medium text-foreground">
                    {log?.changes?.status_change?.to ? 'Active' : 'Inactive'}
                  </span>
                </p>
              )}

              {(log?.employeeCountBefore !== null || log?.employeeCountAfter !== null) && (
                <p className="text-xs">
                  Employee count: {log?.employeeCountBefore || 0} → {log?.employeeCountAfter || 0}
                </p>
              )}

              <p className="text-xs">
                Performed by: <span className="font-medium">{log?.performedBy}</span>
              </p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default AuditLogsList;
